import { CheckCircle2, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Markdown } from '@/components/Markdown';
import { Alert, Badge, Button, ButtonLink, Card, QueryError, Spinner } from '@/components/ui';
import { cn, errorMessage } from '@/lib/utils';
import type { CourseModule, QuizQuestion, QuizResult } from '@/types/db';
import { useCourse, useMarkModuleComplete, useMyProgress, useQuiz, useSubmitQuiz } from './api';

function shuffle<T>(items: T[], seed: string): T[] {
  // Deterministic per quiz so re-renders do not reorder questions mid-attempt.
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    h = (h * 1103515245 + 12345) >>> 0;
    const j = h % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function QuizRunner({ module, onPassed }: { module: CourseModule; onPassed: () => void }) {
  const { data, isPending, isError, error } = useQuiz(module.id);
  const submit = useSubmitQuiz();
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [attemptKey, setAttemptKey] = useState(0);

  const questions = useMemo<QuizQuestion[]>(
    () => (data ? (data.quiz.shuffle_questions ? shuffle(data.questions, `${data.quiz.id}:${attemptKey}`) : data.questions) : []),
    [data, attemptKey],
  );

  if (isPending) return <Spinner />;
  if (isError) return <QueryError error={error} />;

  const { quiz, attempts } = data;
  const best = attempts.reduce((m, a) => Math.max(m, Number(a.score)), 0);
  const passedBefore = attempts.some((a) => a.passed);
  const attemptsLeft = quiz.max_attempts ? quiz.max_attempts - attempts.length : null;
  const unanswered = questions.filter((q) => !(answers[q.id]?.length)).length;
  const resultFor = (id: string) => result?.results.find((r) => r.question_id === id);

  const toggle = (q: QuizQuestion, optionId: string) => {
    if (result) return;
    const current = answers[q.id] ?? [];
    const next = q.kind === 'multiple' ? (current.includes(optionId) ? current.filter((o) => o !== optionId) : [...current, optionId]) : [optionId];
    setAnswers({ ...answers, [q.id]: next });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-sm">
        <Badge tone="neutral">Passing score {quiz.passing_score}%</Badge>
        {attempts.length > 0 && <Badge tone={passedBefore ? 'success' : 'neutral'}>Best {Math.round(best)}%</Badge>}
        {attemptsLeft !== null && <Badge tone={attemptsLeft <= 1 ? 'warning' : 'neutral'}>{attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} left</Badge>}
      </div>

      {result && (
        <Alert tone={result.passed ? 'success' : 'danger'} title={result.passed ? `Passed — ${Math.round(result.score)}%` : `Not passed — ${Math.round(result.score)}%`}>
          {result.passed
            ? 'This module is complete.'
            : result.answers_revealed
              ? 'No attempts remain. Review the explanations below and contact your educator.'
              : 'Review the questions marked incorrect and try again.'}
        </Alert>
      )}

      {questions.map((q, index) => {
        const r = resultFor(q.id);
        return (
          <Card key={q.id} className={cn('p-4', r && (r.correct ? 'border-emerald-300 dark:border-emerald-500/40' : 'border-red-300 dark:border-red-500/40'))}>
            <div className="mb-3 flex items-start gap-2">
              <span className="font-semibold text-stone-400">{index + 1}.</span>
              <Markdown className="flex-1 font-medium">{q.prompt_md}</Markdown>
              {r && (r.correct ? <CheckCircle2 className="size-5 text-emerald-600" /> : <XCircle className="size-5 text-red-600" />)}
            </div>
            {q.kind === 'multiple' && <p className="-mt-2 mb-2 text-xs text-stone-500">Select all that apply</p>}
            <div className="grid gap-2">
              {q.options.map((o) => {
                const selected = answers[q.id]?.includes(o.id);
                const isCorrect = r?.correct_option_ids?.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={Boolean(result)}
                    onClick={() => toggle(q, o.id)}
                    aria-pressed={selected}
                    className={cn(
                      'flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm',
                      selected ? 'border-stone-900 bg-stone-100 dark:border-stone-200 dark:bg-ink-800' : 'border-stone-200 dark:border-stone-700',
                      isCorrect && 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15',
                    )}
                  >
                    <span className={cn('flex size-5 shrink-0 items-center justify-center border', q.kind === 'multiple' ? 'rounded' : 'rounded-full', selected ? 'border-stone-900 bg-stone-900 dark:border-stone-100 dark:bg-stone-100' : 'border-stone-300 dark:border-stone-600')}>
                      {selected && <span className="size-2 rounded-full bg-white" />}
                    </span>
                    {o.text}
                  </button>
                );
              })}
            </div>
            {r?.explanation_md && <Markdown className="mt-3 rounded-lg bg-stone-50 p-3 text-sm dark:bg-stone-900">{r.explanation_md}</Markdown>}
          </Card>
        );
      })}

      {submit.isError && <Alert tone="danger">{errorMessage(submit.error)}</Alert>}

      {!result ? (
        <Button
          size="lg"
          className="w-full"
          disabled={unanswered > 0 || attemptsLeft === 0}
          loading={submit.isPending}
          onClick={() =>
            submit.mutate(
              { quizId: quiz.id, answers },
              {
                onSuccess: (res) => {
                  setResult(res);
                  if (res.passed) onPassed();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                },
              },
            )
          }
        >
          {attemptsLeft === 0 ? 'No attempts remaining' : unanswered > 0 ? `${unanswered} question${unanswered > 1 ? 's' : ''} unanswered` : 'Submit answers'}
        </Button>
      ) : (
        !result.passed &&
        !result.answers_revealed && (
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            onClick={() => {
              setResult(null);
              setAnswers({});
              setAttemptKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        )
      )}
    </div>
  );
}

export function ModulePage() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const course = useCourse(courseId);
  const progress = useMyProgress();
  const markComplete = useMarkModuleComplete();
  const [justPassed, setJustPassed] = useState(false);

  if (course.isPending) return <Spinner />;
  if (course.isError) return <QueryError error={course.error} />;

  const modules = course.data.modules;
  const index = modules.findIndex((m) => m.id === moduleId);
  const module = modules[index];
  if (!module) return <QueryError error={new Error('Module not found')} />;
  const prev = modules[index - 1];
  const next = modules[index + 1];
  const isDone = justPassed || Boolean(progress.data?.modules.some((m) => m.module_id === module.id));

  const goNext = () => navigate(next ? `/learn/courses/${courseId}/modules/${next.id}` : `/learn/courses/${courseId}`);

  return (
    <div>
      <Link to={`/learn/courses/${courseId}`} className="mb-3 inline-flex text-sm text-stone-500 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white">
        ← {course.data.course.title}
      </Link>
      <header className="mb-4">
        <div className="text-sm text-stone-500">
          Module {index + 1} of {modules.length}
        </div>
        <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight">{module.title}</h1>
      </header>

      {module.kind === 'quiz' ? (
        <>
          {module.body_md && <Markdown className="mb-4">{module.body_md}</Markdown>}
          <QuizRunner module={module} onPassed={() => setJustPassed(true)} />
        </>
      ) : (
        <Card className="p-4 sm:p-6">
          {module.video_url && (
            <div className="mb-4 aspect-video overflow-hidden rounded-lg bg-black">
              <video src={module.video_url} controls className="size-full" preload="metadata" />
            </div>
          )}
          <Markdown>{module.body_md}</Markdown>
        </Card>
      )}

      {markComplete.isError && <Alert tone="danger" className="mt-4">{errorMessage(markComplete.error)}</Alert>}
      {markComplete.data?.course_complete && (
        <Alert tone="success" title="Course complete" className="mt-4">
          Your completion and CE hours have been recorded.
        </Alert>
      )}

      <div className="mt-6 flex items-center gap-2">
        {prev ? (
          <ButtonLink variant="ghost" to={`/learn/courses/${courseId}/modules/${prev.id}`} icon={<ChevronLeft className="size-4" />}>
            Previous
          </ButtonLink>
        ) : (
          <span />
        )}
        <div className="ml-auto flex gap-2">
          {module.kind !== 'quiz' && !isDone && (
            <Button loading={markComplete.isPending} onClick={() => markComplete.mutate(module.id, { onSuccess: (r) => !r.course_complete && goNext() })} icon={<CheckCircle2 className="size-4" />}>
              Mark complete{next ? ' & continue' : ''}
            </Button>
          )}
          {(isDone || module.kind === 'quiz') && (
            <Button variant={isDone ? 'primary' : 'outline'} onClick={goNext} icon={<ChevronRight className="size-4" />}>
              {next ? 'Next module' : 'Back to course'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
